import { QueryClient } from "@tanstack/react-query";

/**
 * Status codes that must never be retried.
 *
 * Retrying these can only make things worse: a 401/403 will fail identically
 * until the session is replaced, and a 429 means the backend is already
 * refusing us, so every extra attempt deepens the hole. Queries that override
 * `retry` should compose this rather than counting failures blindly — the
 * guard's `/users/me` and the products/subscriptions queries each retried on
 * 401 AND 429, turning one failure into three or four requests and feeding the
 * storm behind POC-533.
 */
// 410 (POC-920): a deleted account is permanent — retrying only delays the
// "account deleted" screen.
const NON_RETRYABLE_STATUSES = new Set([400, 401, 403, 404, 410, 422, 429]);

const statusOf = (error: unknown): number | undefined =>
	(error as { response?: { status?: number } } | undefined)?.response?.status;

/** Shared predicate: give up immediately on auth/throttle/client errors. */
export const shouldRetryRequest = (failureCount: number, error: unknown) => {
	const status = statusOf(error);
	if (status !== undefined && NON_RETRYABLE_STATUSES.has(status)) return false;
	return failureCount < 2;
};

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// 30s freshness window. staleTime: 0 meant EVERY component mount
			// refetched every query it touches, so each navigation refired the
			// whole page's requests — and behind a shared IP (mobile carrier
			// NAT, offices, VPNs) those bursts collectively tripped the
			// backend's per-IP rate limit into 429s for innocent users
			// (POCKETSFLOW-FRONTEND-1). Mutations invalidate their queries
			// explicitly (invalidateQueries, ~111 call sites), so
			// fresh-after-write is unaffected; only redundant remount refetches
			// inside the 30s window are skipped.
			//
			// Storefront product/PPP GETs override this with 5 minutes — see
			// src/services/storefront/queries.ts (POC-626). /users/me stays at
			// staleTime: 0 in useGetMe.
			staleTime: 30 * 1000,
			gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
			refetchOnWindowFocus: false,
			// `true` = always refetch on mount once data is older than staleTime.
			// Do NOT use the literal "always": it bypasses staleTime entirely and
			// brings the refetch storm right back.
			refetchOnMount: true,
			refetchOnReconnect: false,
			retry: false,
		},
	},
});
